import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Power, Trash2, X, MapPin } from "lucide-react";
import { api, formatErr } from "../lib/api";
import { useBranch } from "../context/BranchContext";

export default function BranchesSection() {
  const branch = useBranch();
  const navigate = useNavigate();
  const [dialog, setDialog] = useState(false);
  const [busy, setBusy] = useState(null);
  if (!branch?.loaded || branch.branches.length === 0) return null;

  const ent = branch.entitlement || {};
  const limitReached = ent.can_create_branch === false;

  const run = async (id, fn, okMsg) => {
    setBusy(id);
    try { await fn(); toast.success(okMsg); await branch.reload(); }
    catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(null); }
  };

  const toggle = (b) => run(b.id, () => api.post(`/branches/${b.id}/${b.active === false ? "activate" : "deactivate"}`), b.active === false ? "تم تفعيل الفرع" : "تم إيقاف الفرع");
  const remove = (b) => {
    if (!window.confirm(`حذف الفرع «${b.name}» نهائيًا؟ لا يمكن التراجع.`)) return;
    run(b.id, () => api.delete(`/branches/${b.id}`), "تم حذف الفرع");
  };

  return (
    <section className="bg-white rounded-2xl border border-orange-100 p-4 mb-4" data-testid="branches-section">
      <div className="flex items-center justify-between mb-1">
        <div className="font-extrabold">الفروع</div>
        <button
          onClick={() => {
            if (limitReached) {
              toast.error("باقتك الحالية لا تشمل فرعًا إضافيًا — تواصل مع الإدارة لإضافة فرع");
            } else {
              setDialog(true);
            }
          }}
          className={`pill px-3 py-2 text-xs font-bold inline-flex items-center gap-1 ${limitReached ? "bg-gray-100 text-gray-400 border border-gray-200" : "brand-grad text-white"}`}
          data-testid="add-branch-btn"
        >
          <Plus size={14}/> إضافة فرع
        </button>
      </div>
      <div className="text-xs text-gray-500 mb-3" data-testid="branch-entitlement-text">
        الفروع الفعّالة: <span className="num-badge font-bold text-gray-800">{ent.active_branch_count}</span> من <span className="num-badge font-bold text-gray-800">{ent.max_active_branches}</span>
        {" "}(<span className="num-badge">{ent.included_branches}</span> ضمن الباقة{ent.additional_branch_limit > 0 && <> + <span className="num-badge">{ent.additional_branch_limit}</span> إضافي</>})
      </div>
      {limitReached && (
        <div className="rounded-xl bg-amber-50 border border-amber-100 text-amber-800 text-xs font-bold px-3 py-2 mb-3" data-testid="branch-limit-notice">
          باقتك الحالية لا تشمل فرعًا إضافيًا. لإضافة فروع جديدة تواصل مع إدارة المنصة.
        </div>
      )}
      <div className="space-y-2">
        {branch.branches.map((b) => (
          <div key={b.id} className="rounded-2xl border border-orange-100 bg-orange-50/40 p-3" data-testid={`branch-row-${b.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-extrabold flex items-center gap-2 flex-wrap">
                  <span data-testid={`branch-name-${b.id}`}>{b.name}</span>
                  <span className={`pill text-[10px] font-bold px-2 py-0.5 ${b.is_main ? "bg-green-100 text-green-700" : "bg-orange-100 text-orange-700"}`} data-testid={`branch-kind-${b.id}`}>
                    {b.is_main ? "الفرع الرئيسي — ضمن الباقة" : "فرع إضافي"}
                  </span>
                  <span
                    className={
                      "pill text-[10px] font-bold px-2 py-0.5 "
                      + (b.active === false
                        ? "bg-red-50 text-red-700 border border-red-100"
                        : "bg-emerald-50 text-emerald-700 border border-emerald-100")
                    }
                    data-testid={`branch-status-${b.id}`}
                  >
                    {b.active === false ? "متوقف" : "فعّال"}
                  </span>
                </div>
                {b.address && <div className="text-xs text-gray-500 mt-1 inline-flex items-center gap-1"><MapPin size={11}/> {b.address}</div>}
                {b.phone && <div className="text-xs text-gray-500 num-badge">{b.phone}</div>}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  onClick={() => navigate(`/dashboard/settings/branches/${b.id}`)}
                  className={
                    "pill bg-white border border-orange-100 px-3 py-2 text-xs font-bold "
                    + "inline-flex items-center gap-1 text-gray-700"
                  }
                  data-testid={`branch-edit-${b.id}`}
                >
                  <Pencil size={15}/> تعديل
                </button>
                {!b.is_main && (
                  <>
                    <button
                      disabled={busy === b.id}
                      onClick={() => toggle(b)}
                      className={
                        "grid h-9 w-9 place-items-center rounded-xl border "
                        + (b.active === false
                          ? "border-red-100 bg-red-50 text-red-600"
                          : "border-green-100 bg-green-50 text-green-700")
                      }
                      title={b.active === false ? "تفعيل" : "إيقاف"}
                      data-testid={`branch-toggle-${b.id}`}
                    >
                      <Power size={15}/>
                    </button>
                    <button disabled={busy === b.id} onClick={() => remove(b)} className="w-9 h-9 rounded-xl bg-white border border-gray-200 grid place-items-center text-gray-500" title="حذف" data-testid={`branch-delete-${b.id}`}><Trash2 size={15}/></button>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      {dialog && (
        <BranchDialog
          onClose={() => setDialog(false)}
          onSaved={() => {
            setDialog(false);
            branch.reload();
          }}
        />
      )}
    </section>
  );
}

function BranchDialog({ onClose, onSaved }) {
  const [f, setF] = useState({ name: "", address: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const u = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    if (!f.name.trim()) return toast.error("اسم الفرع مطلوب");
    setBusy(true);
    try {
      const payload = { name: f.name.trim(), address: f.address, phone: f.phone };
      await api.post("/branches", payload);
      toast.success("تم الحفظ"); onSaved();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-40 bg-black/50 grid place-items-end sm:place-items-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-white rounded-3xl p-5 rise-in" data-testid="branch-dialog">
        <div className="flex items-center justify-between mb-3">
          <div className="font-extrabold text-lg">إضافة فرع</div>
          <button onClick={onClose} className="w-9 h-9 rounded-xl bg-gray-100 grid place-items-center" data-testid="branch-dialog-close"><X size={18}/></button>
        </div>
        <div className="space-y-2">
          <Field label="اسم الفرع" v={f.name} on={(v) => u("name", v)} tid="branch-name-input" />
          <Field label="العنوان (اختياري)" v={f.address} on={(v) => u("address", v)} tid="branch-address-input" />
          <Field label="رقم الجوال (اختياري)" v={f.phone} on={(v) => u("phone", v)} tid="branch-phone-input" />
        </div>
        <div className="mt-2 text-[11px] text-gray-500">
          تُضبط ساعات العمل وخيارات التشغيل من صفحة الفرع بعد إنشائه.
        </div>
        <button disabled={busy} onClick={save} className="mt-4 w-full pill brand-grad text-white font-bold py-3 tap-target disabled:opacity-60" data-testid="branch-save-btn">
          {busy ? "جاري…" : "حفظ"}
        </button>
      </div>
    </div>
  );
}

function Field({ label, v, on, tid }) {
  return (
    <label className="block">
      <span className="text-xs font-bold text-gray-600">{label}</span>
      <input value={v} onChange={(e) => on(e.target.value)} data-testid={tid}
        className="mt-1 w-full h-12 rounded-2xl bg-gray-50 border border-gray-200 px-4 outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100" />
    </label>
  );
}
